/**
 * @file functions/getOrderToken.ts
 *
 * Fetches the order token.
 *
 * This function checks if the order state already has a token. If it does, it returns the existing order
 * token. If not, it makes a POST request to create a new order and retrieves the token from the response.
 *
 * @param orderState - The state of the order containing the order token.
 * @param orderDispatch - The dispatch function from the order context.
 * @param resetOrderToken - If true, forces creation of a new token.
 * @param updateState - If true, updates the context with the new order (default: true).
 *
 * @returns {Promise<object>} - The result of the order token fetch operation.
 */

// 3rd Party Imports
import { getSession } from 'next-auth/react';

// Context
import { updateOrder } from '@/lib/context/orderActions';
import { OrderActions } from '@/lib/context/orderActions';

// Functions
import { convertToOrderState } from '@/lib/functions/convertToOrderState';
import { logMessage } from '@/lib/functions/logMessage';
import { migrateOrder } from '@/lib/functions/migrateOrder';
import { persistOrderState } from '@/lib/functions/persistOrderState';

// Types
import type { OrderState } from '@/lib/types/orderState';

// NOTE: There is an issue where letting react have a customer token type causes
// the cart to not be able to be updated. This is a temporary fix until we can
// figure out the root cause of the issue.
const allowedCustomerTokenType = false;

export async function getOrderToken(
  orderState: OrderState,
  orderDispatch: React.Dispatch<OrderActions>,
  resetOrderToken: boolean = false,
  updateState: boolean = true,
): Promise<{ okay: boolean, token?: string | null, tokenType?: 'customer' | 'guest', message?: string }> {

  // Get the session to access the authentication bearer token
  const session = await getSession();
  const bearerToken = session?.token || null;

  // Return existing cart from the order context (if set)
  const existingOrderToken: string | null = orderState.token;
  if (existingOrderToken !== null && !resetOrderToken) {
    return { okay: true, token: existingOrderToken };
  }

  // Else, get a new one
  try {
    const headers: Record<string, string> = {};
    if (allowedCustomerTokenType && bearerToken) {
      headers['Authorization'] = `Bearer ${bearerToken}`;
    }

    const syliusOrderResponse = await fetch('/api/orders/', {
      method: 'POST',
      headers,
    });

    if (!syliusOrderResponse.ok) {
      throw new Error(`Error: ${syliusOrderResponse.statusText}`);
    }

    const orderPayload = await syliusOrderResponse.json();
    const orderToken = orderPayload.tokenValue;
    const orderTokenType = (allowedCustomerTokenType && bearerToken) ? 'customer' : 'guest';
    const orderStateObject = convertToOrderState(orderPayload, orderTokenType);

    // If already authenticated, associate the new cart with the customer immediately
    if (bearerToken) {
      try {
        await migrateOrder(orderToken, bearerToken);
      } catch (err) {
        logMessage('Order migration failed after token creation: ' + err, 'warning');
      }
    }

    if (orderPayload) {
      // Only update state if updateState flag is true
      if (updateState) {
        updateOrder(orderDispatch, orderStateObject);
        if (resetOrderToken) {
          persistOrderState({ ...orderState, ...orderStateObject });
        }
      }
      return { okay: true, token: orderToken, tokenType: orderTokenType };
    } else {
      logMessage('Error in POST /api/orders: Token value not found in the response', 'error');
      return { okay: false, message: 'Failed to fetch order token' };
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      logMessage('Exception in POST /api/orders: ' + error.message, 'error');
    } else {
      logMessage('Unknown error in POST /api/orders', 'error');
    }
    return { okay: false, message: 'Failed to fetch order token' };
  }
}
